import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { ListVideo, Image } from 'lucide-react';
import type { Schedule } from '@/hooks/signage';

interface ScheduleCalendarProps {
  schedules: Schedule[];
  onScheduleClick: (schedule: Schedule) => void;
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

// Color palette for schedules
const COLORS = [
  'bg-blue-500/20 border-blue-500 text-blue-700 dark:text-blue-300',
  'bg-green-500/20 border-green-500 text-green-700 dark:text-green-300',
  'bg-purple-500/20 border-purple-500 text-purple-700 dark:text-purple-300',
  'bg-orange-500/20 border-orange-500 text-orange-700 dark:text-orange-300',
  'bg-pink-500/20 border-pink-500 text-pink-700 dark:text-pink-300',
  'bg-cyan-500/20 border-cyan-500 text-cyan-700 dark:text-cyan-300',
  'bg-yellow-500/20 border-yellow-500 text-yellow-700 dark:text-yellow-300',
  'bg-red-500/20 border-red-500 text-red-700 dark:text-red-300',
];

export function ScheduleCalendar({ schedules, onScheduleClick }: ScheduleCalendarProps) {
  // Assign colors to schedules
  const scheduleColors = useMemo(() => {
    const colorMap = new Map<string, string>();
    schedules.forEach((s, i) => {
      colorMap.set(s.id, COLORS[i % COLORS.length]);
    });
    return colorMap;
  }, [schedules]);

  // Parse time string to hours (decimal)
  const parseTime = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours + minutes / 60;
  };

  // Get schedule blocks for a specific day
  const getScheduleBlocks = (dayIndex: number) => {
    return schedules
      .filter(s => s.is_active && s.days_of_week.includes(dayIndex))
      .map(s => {
        const startHour = parseTime(s.start_time);
        let endHour = parseTime(s.end_time);

        // Handle overnight schedules
        const isOvernight = endHour < startHour;

        return {
          schedule: s,
          startHour,
          endHour: isOvernight ? 24 : endHour,
          isOvernight,
          color: scheduleColors.get(s.id) || COLORS[0],
        };
      });
  };

  // Get overnight continuation blocks (schedules that started previous day)
  const getOvernightBlocks = (dayIndex: number) => {
    const prevDayIndex = dayIndex === 0 ? 6 : dayIndex - 1;
    return schedules
      .filter(s => {
        if (!s.is_active || !s.days_of_week.includes(prevDayIndex)) return false;
        const startHour = parseTime(s.start_time);
        const endHour = parseTime(s.end_time);
        return endHour < startHour; // Is overnight
      })
      .map(s => {
        const endHour = parseTime(s.end_time);
        return {
          schedule: s,
          startHour: 0,
          endHour,
          isOvernight: true,
          color: scheduleColors.get(s.id) || COLORS[0],
        };
      });
  };

  const formatHour = (hour: number): string => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  const hourHeight = 48; // pixels per hour
  const dayWidth = 140; // pixels per day column

  return (
    <Card>
      <CardContent className="p-0">
        <ScrollArea className="w-full">
          <div className="flex">
            {/* Time column */}
            <div className="flex-shrink-0 w-16 border-r bg-muted/30">
              <div className="h-10 border-b" /> {/* Header spacer */}
              {HOURS.map(hour => (
                <div
                  key={hour}
                  className="relative border-b text-xs text-muted-foreground"
                  style={{ height: hourHeight }}
                >
                  <span className="absolute -top-2 right-2 bg-background px-1">
                    {formatHour(hour)}
                  </span>
                </div>
              ))}
            </div>

            {/* Day columns */}
            <div className="flex">
              {DAYS.map((day, dayIndex) => {
                const blocks = [
                  ...getOvernightBlocks(dayIndex),
                  ...getScheduleBlocks(dayIndex),
                ];

                return (
                  <div
                    key={day}
                    className="flex-shrink-0 border-r last:border-r-0"
                    style={{ width: dayWidth }}
                  >
                    {/* Day header */}
                    <div className="h-10 border-b flex items-center justify-center font-medium text-sm bg-muted/30">
                      {day.slice(0, 3)}
                    </div>

                    {/* Time slots */}
                    <div className="relative" style={{ height: HOURS.length * hourHeight }}>
                      {/* Grid lines */}
                      {HOURS.map(hour => (
                        <div
                          key={hour}
                          className="absolute w-full border-b border-dashed border-muted"
                          style={{ top: hour * hourHeight, height: hourHeight }}
                        />
                      ))}

                      {/* Schedule blocks */}
                      {blocks.map((block, i) => {
                        const top = block.startHour * hourHeight;
                        const height = (block.endHour - block.startHour) * hourHeight;

                        return (
                          <div
                            key={`${block.schedule.id}-${i}`}
                            className={`absolute left-1 right-1 rounded-md border-l-2 cursor-pointer transition-opacity hover:opacity-80 overflow-hidden ${block.color}`}
                            style={{
                              top,
                              height: Math.max(height, 20),
                            }}
                            onClick={() => onScheduleClick(block.schedule)}
                          >
                            <div className="p-1">
                              <div className="text-xs font-medium truncate">
                                {block.schedule.name}
                              </div>
                              {height > 40 && (
                                <div className="flex items-center gap-1 mt-0.5">
                                  {block.schedule.playlist ? (
                                    <ListVideo className="w-3 h-3" />
                                  ) : (
                                    <Image className="w-3 h-3" />
                                  )}
                                  <span className="text-xs truncate opacity-75">
                                    {block.schedule.playlist?.name ||
                                      block.schedule.content_item?.name}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {/* Legend */}
        {schedules.length > 0 && (
          <div className="border-t p-3">
            <div className="flex flex-wrap gap-2">
              {schedules.map(s => (
                <Badge
                  key={s.id}
                  variant="outline"
                  className={`cursor-pointer ${scheduleColors.get(s.id)} ${!s.is_active ? 'opacity-50' : ''}`}
                  onClick={() => onScheduleClick(s)}
                >
                  {s.name}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
